/*
 * Two jobs: pin the jump bar under the masthead past a scroll threshold, and highlight whichever
 * product section is currently in view.
 *
 * Pinning is done by hand (`position: fixed`, toggled by scroll position) rather than
 * `position: sticky`. Sticky was the first attempt and measured broken in this theme: the bar
 * sits inside Horizon's `main.content-for-layout`, which is `display: flex; flex-direction:
 * column`, and two levels of that flex nesting is enough to stop sticky's containing-block math
 * from engaging at all here -- confirmed by reparenting the same element to a direct child of
 * `.page-wrapper` in a live test, where sticky worked immediately. A theme section cannot render
 * outside its own `.shopify-section` wrapper, so the fix is a manual pin instead of a reparent.
 *
 * The links are still plain anchors to each product section -- clicking one scrolls the page,
 * nothing is hidden behind a click, per the deployment brief's "no tabs" call.
 */
class NastyTvJump extends HTMLElement {
  connectedCallback() {
    this.sentinel = this.previousElementSibling;
    this.spacer = document.createElement('div');
    this.spacer.className = 'nasty-g-tv-hero__jump-spacer';
    this.after(this.spacer);

    this.links = [...this.querySelectorAll('[data-tv-jump-link]')];
    this.targets = this.links
      .map((link) => document.getElementById(link.getAttribute('href').slice(1)))
      .filter(Boolean);

    if (this.sentinel) {
      // Horizon's own masthead re-measures `--header-height` live (a warning banner can wrap to a
      // second line at a merchant's discretion), so this reads it fresh on every check rather than
      // caching it once.
      this.#checkPin();

      // `window` never fires `scroll` at 990px and up: assets/base.css makes `html`/`body`
      // `overflow: hidden` there and `.page-wrapper` the real scrolling element instead, the same
      // layout choice that broke `position: sticky` in the first place. Listening on both covers
      // narrow viewports (where the document itself scrolls) and wide ones (where the wrapper
      // does) without needing to know which is active.
      this.pageWrapper = this.closest('.page-wrapper') || document.querySelector('.page-wrapper');
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      window.addEventListener('resize', this.#onScroll, { passive: true });
      this.pageWrapper?.addEventListener('scroll', this.#onScroll, { passive: true });
    }

    if (this.targets.length > 0) {
      this.observer = new IntersectionObserver(this.#onIntersect, {
        // Shrinks the observed viewport to a thin band just under the masthead and jump bar, so a
        // section becomes "current" the moment its heading crosses that band, rather than
        // whenever any sliver of it is technically on screen (which would light up two segments
        // at once on a tall product block).
        rootMargin: '-45% 0px -50% 0px',
      });
      for (const target of this.targets) this.observer.observe(target);
      this.#setActive(this.targets[0].id);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    this.pageWrapper?.removeEventListener('scroll', this.#onScroll);
    this.spacer?.remove();
  }

  #onScroll = () => {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      this.#checkPin();
    });
  };

  #checkPin() {
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
    const shouldPin = this.sentinel.getBoundingClientRect().top <= headerHeight;
    if (shouldPin === this._pinned) return;
    this._pinned = shouldPin;
    this.classList.toggle('is-pinned', shouldPin);
    this.spacer.style.height = shouldPin ? `${this.offsetHeight}px` : '0';
  }

  #onIntersect = (entries) => {
    const visible = entries.find((entry) => entry.isIntersecting);
    if (visible) this.#setActive(visible.target.id);
  };

  #setActive(id) {
    for (const link of this.links) {
      const isActive = link.getAttribute('href') === `#${id}`;
      link.classList.toggle('nasty-g-tv-hero__jump-link--active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('nasty-tv-jump')) {
  customElements.define('nasty-tv-jump', NastyTvJump);
}
